import { useMemo, useRef, useState } from 'react';
import {
    Button, Card, CardBody, CardHeader, Input, SegmentedControl, Select, Textarea,
} from '../primitives';
import {
    AREA_OPTIONS, FREQUENCY_OPTIONS, Idea, IdeaType, Persona, ROLE_OPTIONS,
    TYPE_OPTIONS, formatHours, hoursPerYear,
} from '../data/improvementBoard';

/**
 * Intake form for the Improvement Board.
 *
 * The field set is the point of the exercise. Submission quality comes from
 * asking for observable facts, a quantified impact and the current workaround -
 * not from a bigger free-text box:
 *
 *  - "what happens" and "what should happen" are separate questions, because
 *    someone who cannot articulate a solution can almost always describe what
 *    they see;
 *  - the workaround question reveals the real cost, and occasionally reveals
 *    that the feature already exists;
 *  - people x frequency x minutes forces a reality check, and showing the
 *    resulting hours/year turns "this wastes time" into a number.
 *
 * Every field is required with a minimum length, so a one-word submission
 * cannot reach the table.
 */

type Limits = { min: number, max: number };

const LIMITS = {
    title: { min: 10, max: 80 },
    impact: { min: 100, max: 1200 },
    whatHappens: { min: 100, max: 1200 },
    whatShould: { min: 80, max: 800 },
    repro: { min: 40, max: 600 },
    whatIsIt: { min: 100, max: 1200 },
    whoAndWhy: { min: 100, max: 1000 },
    today: { min: 60, max: 800 },
} satisfies Record<string, Limits>;

type TextKey = keyof typeof LIMITS;

const BLANK = {
    title: '', area: '', roles: '', peopleAffected: '', frequency: '', minutesEach: '',
    impact: '', whatHappens: '', whatShould: '', repro: '', whatIsIt: '', whoAndWhy: '', today: '',
};

type Values = typeof BLANK;

type ImprovementFormProps = {
    author: Persona,
    onSubmit: (idea: Idea) => void,
}

export const ImprovementForm = ({ author, onSubmit }: ImprovementFormProps) => {
    const [type, setType] = useState<IdeaType>('bug');
    const [values, setValues] = useState<Values>(BLANK);
    const [showErrors, setShowErrors] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const isBug = type === 'bug';
    // Only the branch in view is validated or submitted, so switching type
    // never carries a stale answer through.
    const branchKeys: TextKey[] = useMemo(
        () => (isBug ? ['whatHappens', 'whatShould', 'repro'] : ['whatIsIt', 'whoAndWhy', 'today']),
        [isBug],
    );

    const set = (key: keyof Values) => (value: string) =>
        setValues(prev => ({ ...prev, [key]: value }));

    const people = Number(values.peopleAffected);
    const minutes = Number(values.minutesEach);

    const estimate = useMemo(() => {
        if (!people || !minutes || !values.frequency) return null;
        return hoursPerYear({ peopleAffected: people, minutesEach: minutes, frequency: values.frequency });
    }, [people, minutes, values.frequency]);

    /** One entry per invalid field. Empty means the form is submittable. */
    const errors = useMemo(() => {
        const out: Partial<Record<keyof Values, string>> = {};

        const checkText = (key: TextKey) => {
            const text = values[key].trim();
            const { min, max } = LIMITS[key];
            if (text.length === 0) out[key] = 'This is required.';
            else if (text.length < min) out[key] = `Please add a bit more - ${min - text.length} characters short of ${min}.`;
            else if (text.length > max) out[key] = `Too long by ${text.length - max} characters.`;
        };

        checkText('title');
        checkText('impact');
        branchKeys.forEach(checkText);

        if (!values.area) out.area = 'Pick the part of Maintener this is about.';
        if (!values.roles) out.roles = 'Pick who this affects.';
        if (!values.frequency) out.frequency = 'Pick how often this happens.';
        if (!Number.isInteger(people) || people < 1 || people > 500) {
            out.peopleAffected = 'Enter a whole number between 1 and 500.';
        }
        if (!Number.isInteger(minutes) || minutes < 1 || minutes > 480) {
            out.minutesEach = 'Enter a whole number of minutes between 1 and 480.';
        }
        return out;
    }, [values, branchKeys, people, minutes]);

    const errorCount = Object.keys(errors).length;

    /** Live counter while valid; the primitive swaps it for the error when set. */
    const counterHint = (key: TextKey, guidance: string) => {
        const length = values[key].trim().length;
        const { min, max } = LIMITS[key];
        return length === 0 ? guidance : `${length} / ${min} minimum, ${max} maximum`;
    };

    const errorFor = (key: keyof Values) => (showErrors ? errors[key] : undefined);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (errorCount > 0) {
            setShowErrors(true);
            // Send focus to the first thing that needs attention rather than
            // making the user hunt for it.
            const invalid = formRef.current?.querySelector<HTMLElement>('[data-ds-invalid="true"]');
            const field = invalid?.parentElement?.querySelector<HTMLElement>('input, textarea, select');
            (field ?? invalid)?.focus();
            return;
        }

        const branch = isBug
            ? { whatHappens: values.whatHappens.trim(), whatShould: values.whatShould.trim(), repro: values.repro.trim() }
            : { whatIsIt: values.whatIsIt.trim(), whoAndWhy: values.whoAndWhy.trim(), today: values.today.trim() };

        onSubmit({
            id: `idea-${Date.now()}`,
            title: values.title.trim(),
            type,
            area: values.area,
            roles: values.roles,
            peopleAffected: people,
            frequency: values.frequency,
            minutesEach: minutes,
            impact: values.impact.trim(),
            ...branch,
            submittedBy: author.name,
            submittedAt: new Date().toISOString().slice(0, 10),
            votes: [],
        });

        setValues(BLANK);
        setShowErrors(false);
    };

    return (
        <Card>
            <CardHeader>
                <h2 className="ds-section-title">Submit an improvement</h2>
                <p className="ds-muted">
                    Everything here is required. The more concrete you are, the easier it is to act on -
                    write it as if explaining to a colleague who has never seen the screen.
                </p>
            </CardHeader>
            <CardBody>
                <form ref={formRef} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <span className="ds-label">What kind of thing is this?</span>
                        <SegmentedControl
                            label="Type of submission"
                            options={TYPE_OPTIONS}
                            value={type}
                            onChange={setType}
                        />
                    </div>

                    <Input
                        label="One-line summary"
                        required
                        value={values.title}
                        onChange={e => set('title')(e.target.value)}
                        maxLength={LIMITS.title.max}
                        placeholder={isBug
                            ? 'e.g. Handover report loses unsaved notes when the dialog is closed'
                            : 'e.g. Show MXI part availability directly on the task card'}
                        hint={counterHint('title', 'A colleague should understand the gist from this line alone.')}
                        error={errorFor('title')}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Select
                            label="Which part of Maintener?"
                            required
                            options={[{ value: '', label: 'Choose an area…' }, ...AREA_OPTIONS]}
                            value={values.area}
                            onChange={e => set('area')(e.target.value)}
                            error={errorFor('area')}
                        />
                        <Select
                            label="Who does this affect?"
                            required
                            options={[{ value: '', label: 'Choose a group…' }, ...ROLE_OPTIONS]}
                            value={values.roles}
                            onChange={e => set('roles')(e.target.value)}
                            error={errorFor('roles')}
                        />
                    </div>

                    {isBug ? (
                        <>
                            <Textarea
                                label="What happens now?"
                                required rows={5}
                                value={values.whatHappens}
                                onChange={e => set('whatHappens')(e.target.value)}
                                maxLength={LIMITS.whatHappens.max}
                                placeholder="Describe what you see, step by step. What did you click, what appeared, what did you expect to see instead?"
                                hint={counterHint('whatHappens', 'Describe what you see - not what you think is wrong under the hood.')}
                                error={errorFor('whatHappens')}
                            />
                            <Textarea
                                label="What should happen instead?"
                                required rows={4}
                                value={values.whatShould}
                                onChange={e => set('whatShould')(e.target.value)}
                                maxLength={LIMITS.whatShould.max}
                                placeholder="Describe the behaviour you expected. If you are not sure what the fix is, describe the outcome you need."
                                hint={counterHint('whatShould', 'The outcome you need is enough - you do not have to know the fix.')}
                                error={errorFor('whatShould')}
                            />
                            <Textarea
                                label="How do we make it happen?"
                                required rows={3}
                                value={values.repro}
                                onChange={e => set('repro')(e.target.value)}
                                maxLength={LIMITS.repro.max}
                                placeholder="Which page, which button, which aircraft or work order? Anything that lets someone else reproduce it."
                                hint={counterHint('repro', 'Which page, which button, which data.')}
                                error={errorFor('repro')}
                            />
                        </>
                    ) : (
                        <>
                            <Textarea
                                label="What is it?"
                                required rows={5}
                                value={values.whatIsIt}
                                onChange={e => set('whatIsIt')(e.target.value)}
                                maxLength={LIMITS.whatIsIt.max}
                                placeholder="Describe it as if explaining to a colleague. What would you see on the screen, and what would you do with it?"
                                hint={counterHint('whatIsIt', 'Describe it as if explaining to a colleague.')}
                                error={errorFor('whatIsIt')}
                            />
                            <Textarea
                                label="What problem does it solve, and for whom?"
                                required rows={4}
                                value={values.whoAndWhy}
                                onChange={e => set('whoAndWhy')(e.target.value)}
                                maxLength={LIMITS.whoAndWhy.max}
                                placeholder="Who is stuck today, and what are they stuck on? Be specific about the job it helps with."
                                hint={counterHint('whoAndWhy', 'Name the job it helps with and who does that job.')}
                                error={errorFor('whoAndWhy')}
                            />
                            <Textarea
                                label="How do you do this today?"
                                required rows={3}
                                value={values.today}
                                onChange={e => set('today')(e.target.value)}
                                maxLength={LIMITS.today.max}
                                placeholder="The workaround - another system, a spreadsheet, a phone call, or doing it by hand."
                                hint={counterHint('today', 'The workaround tells us what it really costs today.')}
                                error={errorFor('today')}
                            />
                        </>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Select
                            label="How often does this come up?"
                            required
                            options={[{ value: '', label: 'Choose…' }, ...FREQUENCY_OPTIONS.map(f => ({ value: f.value, label: f.label }))]}
                            value={values.frequency}
                            onChange={e => set('frequency')(e.target.value)}
                            error={errorFor('frequency')}
                        />
                        <Input
                            label="How many people?" required
                            type="number" min={1} max={500}
                            value={values.peopleAffected}
                            onChange={e => set('peopleAffected')(e.target.value)}
                            hint="Roughly how many are affected."
                            error={errorFor('peopleAffected')}
                        />
                        <Input
                            label="Minutes lost each time" required
                            type="number" min={1} max={480}
                            value={values.minutesEach}
                            onChange={e => set('minutesEach')(e.target.value)}
                            hint="Your best estimate."
                            error={errorFor('minutesEach')}
                        />
                    </div>

                    {/* A consequence of the three answers above, not an input - which is
                      * what makes it land. It also prompts people to revisit a wild guess. */}
                    <div
                        className="flex items-baseline gap-2"
                        style={{
                            padding: '8px 12px',
                            borderRadius: 'var(--ds-r-md)',
                            backgroundColor: 'var(--ds-surface-sunken)',
                        }}
                    >
                        <span className="ds-muted">Estimated cost of doing nothing:</span>
                        <strong style={{ fontSize: 'var(--ds-fs-lg)' }}>
                            {estimate === null ? '—' : formatHours(estimate)}
                        </strong>
                        <span className="ds-muted">across everyone affected</span>
                    </div>

                    <Textarea
                        label="What does this unlock?"
                        required rows={4}
                        value={values.impact}
                        onChange={e => set('impact')(e.target.value)}
                        maxLength={LIMITS.impact.max}
                        placeholder="What becomes possible, or stops going wrong? Think about safety, delays, rework and cost - not just the minutes."
                        hint={counterHint('impact', 'What becomes possible, or stops going wrong?')}
                        error={errorFor('impact')}
                    />

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="ds-muted">
                            Submitting as <strong>{author.name}</strong> · {author.role}
                        </span>
                        <div className="flex items-center gap-3">
                            {showErrors && errorCount > 0 && (
                                <span className="ds-hint" data-ds-invalid="true">
                                    {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
                                </span>
                            )}
                            <Button type="submit" variant="primary">Submit to the board</Button>
                        </div>
                    </div>
                </form>
            </CardBody>
        </Card>
    );
};

export default ImprovementForm;
